import { Box, Grid, TextField, Autocomplete, Typography, Button, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { FC, useState, useReducer, useEffect } from 'react';
import { AppDialog } from '../../../components';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { addStaffAction, addStaffResetAction, fetchStaffsAction } from '../../../store/staff';
import { showAlertAction } from '../../../store/alerts';
import CustomNumberField from '../Users/profile/CustomNumberField';
import { http } from '../../../../src/utils';
import { Staff } from 'src/services';
import { fetchPermissions as fetchPermissionsService, Permission } from './services';

type AddStaffFields = 'name' | 'mobile' | 'userLevel';
type AddStaffError = Record<AddStaffFields, Record<string, string>>;
const addStaffErrors: AddStaffError = {
  name: {
    empty: '',
  },
  mobile: {
    empty: '',
  },
  userLevel: {
    empty: 'Please select a user level',
  },
};

type InputBox = {
  value: string;
  error: boolean;
  helperText: string;
};

type AddStaffFieldsState = Record<AddStaffFields, InputBox>;

const defaultObj: InputBox = {
  value: '',
  error: false,
  helperText: '',
};

const initialAddStaffState: AddStaffFieldsState = {
  name: { ...defaultObj },
  mobile: { ...defaultObj },
  userLevel: { ...defaultObj },
};

type Action = {
  type: 'SET' | 'ERROR';
  payload: {
    name: AddStaffFields;
    value: string;
  };
};
const addStaffReducer = (state, action) => {
  const {
    type,
    payload: { name, value },
  } = action;
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: { ...state[name], error: false, value: value, helperText: '' },
      };
    case 'ERROR':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: addStaffErrors[name]['empty'],
        },
      };
    default:
      return state;
  }
};

export interface AddStaffProps {
  children?: React.ReactNode;
  staff?: Staff;
  onClose?: () => void;
}

const userLevels = [
  { label: 'VIEWER', description: 'Read-only access to all resources' },
  { label: 'EDITOR', description: 'Can create and edit resources but cannot delete' },
  { label: 'ADMIN', description: 'Full access to all features' },
  { label: 'RESTRICTED', description: 'Custom permissions with selected access rights' },
];

const AddStaff: FC<AddStaffProps> = ({ onClose, children, staff }) => {
  const { data, success: addStaffSuccess, error: addStaffError, message } = useAppSelector((state) => state.addStaff);
  const dispatchAction = useAppDispatch();
  const [addStaffData, dispatch] = useReducer(addStaffReducer, initialAddStaffState);
  const { name, mobile, userLevel } = addStaffData;
  const [isClose, setClose] = useState<boolean>(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [loadingQr, setLoadingQr] = useState<boolean>(false);
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState<boolean>(false);

  const onChangeHandler = (e) => {
    const {
      target: { name, value },
    } = e;
    if (value.length > 0) {
      dispatch({ type: 'SET', payload: { name, value } });
    } else {
      dispatch({ type: 'ERROR', payload: { name, value } });
    }
  };

  const onUserLevelChange = (event, value) => {
    const newLevel = value ? value.label : '';
    dispatch({ type: 'SET', payload: { name: 'userLevel', value: newLevel } });
    // Clear permissions when changing from RESTRICTED to another level
    if (userLevel.value === 'RESTRICTED' && newLevel !== 'RESTRICTED') {
      setSelectedPermissions([]);
    }
  };

  const submitHandle = () => {
    if (name.value.length && mobile.value.length && userLevel.value.length) {
      const requestPayload: any = {
        name: name.value,
        mobile: mobile.value,
        userLevel: userLevel.value.toUpperCase(),
      };
      
      // Include id when updating an existing staff member
      if (staff?.id) {
        requestPayload.id = staff.id;
      }
      
      // Always include permissions in the request
      // If RESTRICTED, include selected permissions; otherwise, send empty array to clear
      if (userLevel.value.toUpperCase() === 'RESTRICTED') {
        requestPayload.permissions = Array.isArray(selectedPermissions) ? selectedPermissions : [];
      } else {
        // Clear permissions when user level is not RESTRICTED
        requestPayload.permissions = [];
      }
      
      dispatchAction(addStaffAction(requestPayload));
    } else {
      if (!name.value.length) {
        dispatch({ type: 'ERROR', payload: { name: 'name' } });
      }
      if (!mobile.value.length) {
        dispatch({ type: 'ERROR', payload: { name: 'mobile' } });
      }
      if (!userLevel.value.length) {
        dispatch({ type: 'ERROR', payload: { name: 'userLevel', value: '' } });
      }
    }
  };

  const fetchQrCode = () => {
    if (staff || data) {
      setLoadingQr(true);

      http
        .get(`/user/pvt/staff/qr_code?id=${staff?.id ?? data?.id}`)
        .then((response) => {
          if (response.data.success) {
            setQrCodeUrl(response.data.data);
          }
        })
        .catch(() => {
          setQrCodeUrl(null);
        })
        .finally(() => {
          setLoadingQr(false);
        });
    }
  };

  const fetchPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const response = await fetchPermissionsService();
      if (response.success && response.data && Array.isArray(response.data)) {
        setAvailablePermissions(response.data);
      } else {
        setAvailablePermissions([]);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setAvailablePermissions([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handlePermissionChange = (permissionKey: string, checked: boolean) => {
    const currentPermissions = Array.isArray(selectedPermissions) ? selectedPermissions : [];
    if (checked) {
      setSelectedPermissions([...currentPermissions, permissionKey]);
    } else {
      setSelectedPermissions(currentPermissions.filter(key => key !== permissionKey));
    }
  };

  useEffect(() => {
    // Fetch permissions on dialog load
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (staff) {
      dispatch({
        type: 'SET',
        payload: { name: 'name', value: staff.userName || '' },
      });
      dispatch({
        type: 'SET',
        payload: { name: 'mobile', value: (staff.mobile || '').replace('+91', '') },
      });
      dispatch({
        type: 'SET',
        payload: { name: 'userLevel', value: staff.userLevel || 'Admin' },
      });
      
      // Load existing permissions if user is RESTRICTED (case-insensitive check)
      const isRestricted = staff.userLevel && staff.userLevel.toUpperCase() === 'RESTRICTED';
      if (isRestricted && staff.permissions) {
        // Handle different formats: array, JSON string, or object
        let permissions: string[] = [];
        if (Array.isArray(staff.permissions)) {
          permissions = staff.permissions;
        } else if (typeof staff.permissions === 'string') {
          // If permissions is a JSON string, parse it
          try {
            const parsed = JSON.parse(staff.permissions);
            permissions = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            console.error('Error parsing permissions JSON string:', e);
            permissions = [];
          }
        } else if (typeof staff.permissions === 'object') {
          // If permissions is an object, extract the keys
          permissions = Object.keys(staff.permissions);
        }
        setSelectedPermissions(permissions);
      } else {
        setSelectedPermissions([]);
      }
    } else {
      // Clear permissions when adding new user
      setSelectedPermissions([]);
    }
  }, [staff, dispatch]);

  useEffect(() => {
    if (addStaffSuccess) {
      dispatchAction(fetchStaffsAction());
      dispatchAction(addStaffResetAction());
      if(staff) {
        onClose();
      } else {
        fetchQrCode()
      }
     
    } else if (addStaffError && message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }));
      dispatchAction(addStaffResetAction());
    }
  }, [addStaffSuccess, addStaffError, message]);

  return (
    <AppDialog
      id="add-staff"
      title={staff ? 'Edit User' : 'Add User'}
      submitButtonText={staff ? 'UPDATE USER' : 'ADD USER'}
      onClose={onClose}
      close={isClose}
      onSubmit={submitHandle}
      maxWidth="sm"
    >
      <Box component="form" sx={{ '& .MuiTextField-root': { m: 1, width: '25ch' } }} noValidate autoComplete="off">
        <Grid container>
          <Grid item xs>
            <TextField
              size="small"
              name="name"
              value={name.value}
              label="Name"
              error={name.error}
              helperText={name.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <CustomNumberField
              name="mobile"
              value={mobile.value}
              label="Mobile"
              maxLength={13}
              error={mobile.error}
              required
              helperText={mobile.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <Autocomplete
              options={userLevels}
              getOptionLabel={(option) => option.label}
              value={userLevels.find((level) => level.label === userLevel.value) || null}
              onChange={onUserLevelChange}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {option.label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {option.description}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  name="userLevel"
                  value={userLevel.value}
                  error={userLevel.error}
                  helperText={userLevel.helperText}
                  label="User Level"
                  placeholder="Select user level"
                  sx={{ minWidth: 545 }}
                />
              )}
              sx={{ minWidth: 545 }}
            />
          </Grid>
          {userLevel.value === 'RESTRICTED' && (
            <Grid item xs={12} mt={2}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Restricted Permissions:
              </Typography>
              {loadingPermissions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ 
                  maxHeight: 300, 
                  overflowY: 'auto', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1, 
                  p: 2 
                }}>
                  {!Array.isArray(availablePermissions) || availablePermissions.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No permissions available
                    </Typography>
                  ) : (
                    <Grid container spacing={1}>
                      {(() => {
                        const midPoint = Math.ceil(availablePermissions.length / 2);
                        const firstColumn = availablePermissions.slice(0, midPoint);
                        const secondColumn = availablePermissions.slice(midPoint);
                        
                        return (
                          <>
                            <Grid item xs={12} sm={6}>
                              {firstColumn.map((permission) => (
                                <FormControlLabel
                                  key={permission.key}
                                  control={
                                    <Checkbox
                                      checked={Array.isArray(selectedPermissions) && selectedPermissions.includes(permission.key)}
                                      onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                                      size="small"
                                    />
                                  }
                                  label={permission.value || permission.key}
                                  sx={{ display: 'block', mb: 0.5 }}
                                />
                              ))}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {secondColumn.map((permission) => (
                                <FormControlLabel
                                  key={permission.key}
                                  control={
                                    <Checkbox
                                      checked={Array.isArray(selectedPermissions) && selectedPermissions.includes(permission.key)}
                                      onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                                      size="small"
                                    />
                                  }
                                  label={permission.value || permission.key}
                                  sx={{ display: 'block', mb: 0.5 }}
                                />
                              ))}
                            </Grid>
                          </>
                        );
                      })()}
                    </Grid>
                  )}
                </Box>
              )}
            </Grid>
          )}
          {staff && (
            <Grid item xs={12} mt={2} ml={1} >
                <Grid item xs={6} display="flex">
              <Button variant="contained" onClick={fetchQrCode}>
                Show Login QR Code
              </Button>
              </Grid>
            </Grid>
          )}
          {loadingQr && (
            <Grid item xs={12} mt={4} textAlign={'center'}>
              <CircularProgress sx={{mt:5}}/>
            </Grid>
          )}
          {qrCodeUrl && (
            <Grid container spacing={0} mt={4}>
              <Grid item xs={6} >
                <img src={qrCodeUrl} alt="QR Code" style={{ width: '200px', height: '200px', marginLeft:10 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h5">Steps to Use QR Code:</Typography>
                <Typography variant="body1">1. Open Login Screen in mobile App </Typography>
                <Typography variant="body1">2. Tap QR Code icon (Top-right corner)</Typography>
                <Typography variant="body1">3. Scan this QR code</Typography>
                <Typography variant="body1">4. The account is logged in on other device.</Typography>
                </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
    </AppDialog>
  );
};

export default AddStaff;

